/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useState } from "react";
import styled from "styled-components";
import Container from "../../components/Container";
import NavigationHeader from "../../components/NavigationHeader";
import Stack from "../../components/ui/Stack";
import { keepPreviousData } from "@tanstack/react-query";
import Paginator from "../../components/ui/Paginator";
import Dimmed from "../../components/ui/Dimmed";
import PluginCardSkeleton from "../../components/skeleton/PluginCardSkeleton";
import ClientPromotionItem from "../../components/promotion/ClientPromotionItem";
import ClientPromotionsEmptyState from "../../components/promotion/ClientPromotionsEmptyState";
import { useGetClientPromotions } from "../../lib/react-query/useGetClientPromotions";
import { useAuth } from "../../hooks/useAuth";
import { ROUTES } from "../../constants/constants";

const Grid = styled(Stack)`
  grid-template-columns: repeat(auto-fill, minmax(min(314px, 100%), 1fr));  
`;

const ClientPromotions = () => {
  const { isAuthenticated } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const { data, isLoading, isFetching, isPlaceholderData } = useGetClientPromotions(currentPage, {
    enabled: isAuthenticated,
    placeholderData: keepPreviousData,
  });
  const promotions = data?.data || [];
  const totalPages = data?.metadata?.pages_count || 1;

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <Container>
      <NavigationHeader title="My Gift cards" cart clientLogin back={ROUTES.HOME} />
      <Stack $gap="1rem">
        {isLoading && (
          <Grid $gap="0.5rem">
            <PluginCardSkeleton withIncludes />
          </Grid>
        )}
        {!isLoading && promotions.length === 0 && <ClientPromotionsEmptyState />}

        {!isLoading && promotions.length > 0 && (
          <>
            <Dimmed $active={isPlaceholderData}>
              <Grid $gap="0.5rem">
                {promotions.map((p) => (
                  <ClientPromotionItem key={p.id} purchase={p} />
                ))}
              </Grid>
            </Dimmed>
            {totalPages > 1 && (
              <Paginator
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                isLoading={isFetching}
              />
            )}
          </>
        )}
      </Stack>
    </Container>
  );
};

export default ClientPromotions;
